import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { Filterable } from '@common/utilities/Filterable';
import { download, main } from '@core/operators';
import { CurrenciesService } from '@core/services/currencies.service';
import { DataService, OrderDetailRecord, OrderRecord, OrderState, PartnerRecord, ProductRecord } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Worksheet, XLSX } from '@core/utilities/xlsx';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-imports',
  templateUrl: './imports.component.html',
  styleUrls: ['./imports.component.scss']
})
export class ImportsComponent implements OnInit {

  constructor(
    private sync: SynchronizationService,
    private layout: LayoutService,
    private ds: DataService,
    private modal: ModalService,
    private users: UsersService,
    private partners: PartnersService,
    private currencies: CurrenciesService,
    private date: DatePipe) { }

  public orders = new Filterable<OrderRecord>({ grouper: i => [{ name: 'Khách hàng', option: '#' + i.PartnerID }] });
  public title: string;
  public mode: 'notify' | 'warn';

  public products = new Filterable<ProductRecord>({ extractor: i => [i.SKU] });

  public get loading(): boolean {
    return !this.sync.ready(ImportsComponent, CurrenciesService, PartnersService, UsersService);
  }

  public synchronize() {

    let orders: OrderRecord[] = [];

    this.sync.with(ImportsComponent).repeat(

      () => this.ds.orders("unnotified").pipe(map(data => {

        if (data) {
          orders = data.commit(i => i.Details = i.Details.filter(i => i.Quantity > i.Provided).sort((a, b) => a.SKU + "/" + a.Color > b.SKU + "/" + b.Color ? 1 : -1)).filter(i => i.Details.length > 0);
          return true;
        }

        return false;
      })));

    this.sync.when(ImportsComponent).commit(() => {

      if (orders.length < 1) {

        this.sync.with(ImportsComponent).repeat(() => this.ds.orders('incompleted').pipe(map(data => {

          if (data) {

            let deadline = new Date().date().addDays(7).getTime();
            orders = data
              .filter(i => i.State == OrderState.Confirmed)
              .commit(i => i.Details = i.Details.filter(x => x.Quantity > x.Provided && new Date(i.Stamp).date().addDays(x.ExpectedDelay).getTime() < deadline).order(x => new Date(i.Stamp).date().addDays(x.ExpectedDelay).getTime()))
              .filter(i => i.Details.length > 0)
              .order(i => new Date(i.Stamp).date().addDays(i.Details[0].ExpectedDelay).getTime());

            if (orders.length < 1) {

              if (this.orders.empty) this.modal.show(MessageBoxComponent, {
                ignoreBackdropClick: false, state: {
                  closable: true,
                  message: Multilingual.getText("Hiện tại không có đơn cần báo xưởng. Xin vui lòng quay lại sau!"),
                  title: Multilingual.getText("Thông báo"),
                }
              });
            }
            else {

              this.mode = 'warn';
              this.orders.source = orders;
              this.title = 'Danh sách các đơn hàng cần giao trong 7 ngày';
            }

            return true;
          }

          return false;

        })));
      }
      else {

        this.mode = 'notify';
        this.orders.source = orders;
        this.title = 'Danh sách các đơn hàng chưa chốt xưởng';
      }
    });   
  }

  public get now(): Date {
    return new Date();
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('header', 'screen');
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Chốt hàng đặt xưởng");
    });

    this.synchronize();
  }

  public get importOrderName(): string {
    return this.orders.source.length < 1 ? "" :

      this.orders.source.first() == this.orders.source.last()

        ? this.orders.source.first().Tag
        : this.orders.source.first().Tag + "-" + this.orders.source.last().Tag;

  }

  public partner(order: OrderRecord): PartnerRecord {
    return this.partners.get(order.PartnerID);
  }

  public deadline(order: OrderRecord, detail: OrderDetailRecord): Date {
    return new Date(order.Stamp).addDays(detail.ExpectedDelay);
  }

  public className(order: OrderRecord, detail: OrderDetailRecord): string {
    let today = new Date().date().getTime();
    let stamp = new Date(order.Stamp).date().addDays(detail.ExpectedDelay).getTime();

    return stamp <= today ? 'text-danger' : 'text-warning';
  }

  public quantity(order: OrderRecord): number {
    return order.Details.sum(i => i.Quantity - i.Provided);
  }

  public get totalQuantity(): number {
    return this.orders.source.sum(i => this.quantity(i));
  }


  public exportXlsx() {

    this.sync.with(ImportsComponent).commit(done => {
      let xlsx: XLSX = new XLSX();

      let ws: Worksheet = xlsx.worksheet(Multilingual.getText("Đơn đặt hàng (总订单)", "cn"));

      let i: number = 1;

      ws.column("A").align = "left";
      ws.column("B").align = "center";
      ws.column("C").align = "center";
      ws.column("D").align = "center";
      ws.column("E").align = "center";
      ws.column("F").align = "center";
      ws.column("G").align = "center";
      ws.column("H").align = "right";
      ws.column("I").align = "right";
      ws.column("J").align = "right";

      ws.column("F").number = "integer";
      ws.column("H").number = "decimal";
      ws.column("I").number = "decimal";
      ws.column("J").number = "decimal";

      ws.cell("A", i).data = Multilingual.getText("Mã đơn(单)", "cn");
      ws.cell("B", i).data = Multilingual.getText("Khách hàng (客户)", "cn");
      ws.cell("C", i).data = Multilingual.getText("Ngày đặt (订货时间)", "cn");
      ws.cell("D", i).data = Multilingual.getText("Mã hàng (货号)", "cn");
      ws.cell("E", i).data = Multilingual.getText("Màu (颜色)", "cn");
      ws.cell("F", i).data = Multilingual.getText("Số cây (匹数)", "cn");
      ws.cell("G", i).data = Multilingual.getText("Giá xưởng (价格)", "cn") + " (¥/m)";
      ws.cell("H", i).data = Multilingual.getText("Tỷ giá (人民币汇率)", "cn");
      ws.cell("I", i).data = Multilingual.getText("Giá bán (越南盾)", "cn") + " (đ/m)";
      ws.cell("J", i++).data = Multilingual.getText("Hẹn giao (交货时间)", "cn");

      this.orders.source.forEach(order => {

        ws.cell("A", i).data = order.Tag
        ws.cell("B", i).data = this.partner(order).Name.toUpperCase();
        ws.cell("C", i).data = new Date(order.Stamp);

        order.Details.forEach(detail => {
          ws.cell("D", i).data = detail.SKU;
          ws.cell("E", i).data = detail.Color;
          ws.cell("F", i).data = detail.Quantity - detail.Provided;
          ws.cell("G", i).data = detail.ImportUnitPrice;

          ws.cell("H", i).data = Math.round(detail.QuoteConversion);
          ws.cell("I", i).data = detail.QuoteUnitPrice;
          ws.cell("J", i++).data = this.deadline(order, detail);
        });

        i++;
      });

      ws = xlsx.worksheet(Multilingual.getText("Thống kê mã hàng (总编码)", "cn"));
      i = 1;

      ws.column("A").align = "left";
      ws.column("B").align = "center";
      ws.column("C").align = "center";

      ws.column("C").number = "integer";

      ws.cell("A", i).data = Multilingual.getText("Mã hàng (货号)", "cn");
      ws.cell("B", i).data = Multilingual.getText("Màu (颜色)", "cn");
      ws.cell("C", i++).data = Multilingual.getText("Số cây (匹数)", "cn");

      this.orders.source.many(i => i.Details).group(i => i.SKU).forEach(sku => {

        ws.cell("A", i).data = sku.key;

        sku.items.group(i => i.Color).forEach(color => {

          ws.cell("B", i).data = color.key;
          ws.cell("C", i++).data = color.items.sum(x => x.Quantity - x.Provided);
        });

        i++;

      });

      xlsx.generate().subscribe(base64 => {
        download(this.date.transform(new Date(), 'dd-MM-yyyy HH_mm_ss z') + ' (' + this.importOrderName + ").xlsx", base64);
        done();
      });
    });
  }

  public print() {
    print();
  }

  public notify(order: OrderRecord) {
    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: Multilingual.getText("Bạn muốn chốt đơn đặt hàng") + " " + order.Tag + "?",
        title: Multilingual.getText("Xác nhận"),
        buttons: [
          {
            icon: 'fa fa-check', class: 'bg-warning text-white', name: Multilingual.getText('Xác nhận'), click: e => {

              this.sync.with(ImportsComponent).commit(done => this.ds.orders('notify', order).subscribe(results => {
                if (results && results.length == 1) this.orders.source = this.orders.source.filter(x => x != order);
                done();
              }));
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: Multilingual.getText('Quay lại') }
        ]
      }
    });
  }

}
